/**
 * AuthShell — full-bleed branded layout for unauthenticated routes
 * (login, reset-password, future signup).
 *
 * Renders, in z-order:
 *   1. A gradient fallback background (always visible, no flicker if video missing)
 *   2. The marketing hero video on loop, muted, autoplay (drops in when /brand/login-hero.mp4 is present)
 *   3. A dark overlay + backdrop blur so the foreground card stays legible
 *   4. The SoftSync logo lockup centered above the children
 *   5. The page-specific content (e.g. login form Card)
 *
 * Children control their own width via the Card component they pass in.
 */

import type { ReactNode } from 'react';
import { LogoLockup } from '@/components/branding/logo';

export interface AuthShellProps {
  children: ReactNode;
  /** Optional caption under the wordmark. Defaults to 'CRM'. */
  caption?: string | null;
}

export function AuthShell({ children, caption = 'CRM' }: AuthShellProps): JSX.Element {
  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Gradient fallback (always visible) */}
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgb(var(--accent)/0.35),transparent_50%),radial-gradient(circle_at_75%_85%,rgb(var(--accent-2)/0.30),transparent_55%),linear-gradient(180deg,#04141b_0%,#000_100%)]"
      />

      {/* Marketing hero video — gracefully no-ops if the file isn't present yet */}
      <video
        autoPlay
        muted
        loop
        playsInline
        poster="/brand/login-hero-poster.jpg"
        className="absolute inset-0 h-full w-full object-cover"
        aria-hidden
      >
        <source src="/brand/login-hero.mp4" type="video/mp4" />
      </video>

      {/* Dark overlay for legibility */}
      <div aria-hidden className="absolute inset-0 bg-black/55 backdrop-blur-sm" />

      {/* Foreground content */}
      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center p-6">
        <div className="mb-6 flex justify-center">
          <LogoLockup size="xl" caption={caption} />
        </div>
        {children}
      </div>
    </div>
  );
}
